import React, { useMemo, useState, useRef, Fragment } from 'react';
import { CalendarIcon, BuildingIcon, ImageIcon, LayoutGridIcon, MonitorIcon, SendIcon, CheckCircleIcon, AlertCircleIcon, SearchIcon, InboxIcon, ArrowRightIcon, XIcon, ClockIcon, FileTextIcon, PencilIcon, Trash2Icon, ChevronDownIcon, MapPinIcon, DollarSignIcon, StoreIcon } from 'lucide-react';
import { User, Store, BrandRequest, MediaType } from '../data/types';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { PageTransition } from '../components/ui/PageTransition';
import { ReviewInvitationModal } from '../components/brand/ReviewInvitationModal';
import { BrandRequestModal } from '../components/admin/BrandRequestModal';
import { ArtworkUpload } from '../components/booking/ArtworkUpload';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { StoreAvailabilityBrowser } from '../components/brand/StoreAvailabilityBrowser';
import { DashboardCalendar } from '../components/dashboard/DashboardCalendar';
interface BrandDashboardPageProps {
  user: User;
  stores: Store[];
  brandRequests: BrandRequest[];
  onLogout: () => void;
  onStoreSelect: (store: Store) => void;
  onNavigate?: (view: any) => void;
  onRespondToInvitation: (requestId: string, response: 'accepted' | 'rejected', message?: string) => void;
  onSendRequest: (request: any, sendNow?: boolean) => void;
  onUpdateRequest?: (requestId: string, request: any, sendNow?: boolean) => void;
  onRemoveRequest?: (requestId: string) => void;
  activityLog?: any;
}
const mediaTypeIcon: Record<MediaType, React.ReactNode> = {
  'Digital Outposts': <LayoutGridIcon className="w-3.5 h-3.5" data-id="element-1370" />,
  'Digital Drive Aisle': <ImageIcon className="w-3.5 h-3.5" data-id="element-1371" />,
  'Digital Windows': <MonitorIcon className="w-3.5 h-3.5" data-id="element-1372" />
};
function formatWeekDates(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short'
  };
  return `${s.toLocaleDateString('en-ZA', opts)} – ${e.toLocaleDateString('en-ZA', opts)}`;
}
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0
  }).format(amount);
}
function resolvePositionNames(positionIds: string[], stores: Store[]): string[] {
  const names = new Set<string>();
  positionIds.forEach(pid => {
    stores.forEach(store => {
      const pos = store.positions?.find(p => p.id === pid);
      if (pos) names.add(pos.name);
    });
  });
  return Array.from(names);
}
function PositionBadges({
  positionIds,
  stores
}: {
  positionIds?: string[];
  stores: Store[];
}) {
  if (!positionIds || positionIds.length === 0) return null;
  const names = resolvePositionNames(positionIds, stores);
  if (names.length === 0) return null;
  return <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-1373">
      {names.slice(0, 4).map(name => <span key={name} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-surface-warm border border-border text-text-secondary font-medium" data-id="element-1374">
          {name}
        </span>)}
      {names.length > 4 && <span className="text-[10px] text-text-tertiary" data-id="element-1375">
          +{names.length - 4} more
        </span>}
    </div>;
}
export function BrandDashboardPage({
  user,
  stores,
  brandRequests,
  onLogout,
  onStoreSelect,
  onNavigate,
  onRespondToInvitation,
  onSendRequest,
  onUpdateRequest,
  onRemoveRequest,
  activityLog
}: BrandDashboardPageProps) {
  const brandName = user.company || user.name;
  const [selectedInvitation, setSelectedInvitation] = useState<BrandRequest | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [preSelectedStoreIds, setPreSelectedStoreIds] = useState<string[]>([]);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [editingRequest, setEditingRequest] = useState<BrandRequest | null>(null);
  const [artworkModalSlot, setArtworkModalSlot] = useState<any>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    action: () => void;
  }>({
    isOpen: false,
    title: '',
    description: '',
    action: () => {}
  });
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Section refs for scroll-to navigation
  const invitationsRef = useRef<HTMLElement>(null);
  const requestsRef = useRef<HTMLElement>(null);
  const campaignsRef = useRef<HTMLElement>(null);
  const scrollToSection = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const handleRespondToInvitation = (requestId: string, response: 'accepted' | 'rejected', message?: string) => {
    onRespondToInvitation(requestId, response, message);
    showToast(`Invitation ${response === 'accepted' ? 'accepted' : 'declined'} successfully`);
  };
  // Extract booked slots for this brand and group by week
  const myCampaignsByWeek = useMemo(() => {
    const grouped = new Map<number, any[]>();
    stores.forEach(store => {
      store.slots.forEach(slot => {
        if (slot.status === 'booked' && slot.brandName === brandName) {
          if (!grouped.has(slot.weekNumber)) {
            grouped.set(slot.weekNumber, []);
          }
          grouped.get(slot.weekNumber)!.push({
            id: slot.id,
            store,
            slot
          });
        }
      });
    });
    return Array.from(grouped.entries()).map(([weekNumber, campaigns]) => ({
      weekNumber,
      campaigns: campaigns.sort((a, b) => a.store.name.localeCompare(b.store.name))
    })).sort((a, b) => a.weekNumber - b.weekNumber);
  }, [stores, brandName]);
  // Extract pending invitations for this brand
  const pendingInvitations = useMemo(() => {
    return brandRequests.filter(r => r.brandName === brandName && (r.status === 'pending' || r.status === 'brand-accepted') && r.requestedBy === 'woolworths');
  }, [brandRequests, brandName]);
  // Extract brand-initiated requests (submitted by this brand)
  const myRequests = useMemo(() => {
    return brandRequests.filter(r => r.brandName === brandName && r.requestedBy === 'brand');
  }, [brandRequests, brandName]);
  const missingArtworkCount = myCampaignsByWeek.filter(c => c.campaigns.some(c => c.slot.artworkStatus === 'none' || !c.slot.artworkStatus)).length;
  // Add richer stats
  const campaignStats = useMemo(() => {
    const uniqueStores = new Set(myCampaignsByWeek.flatMap(c => c.campaigns).map(c => c.store.id));
    const totalInvestment = myCampaignsByWeek.reduce((sum, c) => sum + c.campaigns.reduce((s, c) => s + c.slot.price, 0), 0);
    const activeWeeks = new Set(myCampaignsByWeek.map(c => c.weekNumber));
    return {
      storeCount: uniqueStores.size,
      totalInvestment,
      weekCount: activeWeeks.size
    };
  }, [myCampaignsByWeek]);
  const handleRequestSpace = (storeId?: string, weekNumber?: number) => {
    if (storeId) {
      setPreSelectedStoreIds([storeId]);
    } else {
      setPreSelectedStoreIds([]);
    }
    setIsRequestModalOpen(true);
  };
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-1376">
      <Header user={user} onLogout={onLogout} currentView="brand-dashboard" onNavigate={onNavigate} stores={stores} onSelectStore={onStoreSelect} onRequestMediaSpace={() => handleRequestSpace()} activityEntries={activityLog?.entries} unreadCount={activityLog?.unreadCount} onMarkAllRead={activityLog?.markAllAsRead} onMarkRead={activityLog?.markAsRead} data-id="element-1377" />

      <PageTransition className="flex-1 flex flex-col" data-id="element-1378">
        {/* Welcome Header */}
        <div className="bg-surface-warm border-b border-border px-6 py-8" data-id="element-1379">
          <div className="max-w-6xl mx-auto flex items-start justify-between" data-id="element-1380">
            <div data-id="element-1381">
              <h1 className="text-3xl font-bold text-text-primary mb-2" data-id="element-1382">
                Welcome back, {brandName}
              </h1>
              <p className="text-text-secondary" data-id="element-1383">
                Track your campaigns, respond to invitations, and manage artwork
                across Woolworths stores.
              </p>
              {myCampaignsByWeek.length > 0 && <div className="flex items-center gap-5 mt-4" data-id="element-1384">
                  <div className="flex items-center gap-1.5 text-sm text-text-secondary" data-id="element-1385">
                    <MapPinIcon className="w-4 h-4 text-text-tertiary" data-id="element-1386" />
                    <span className="font-semibold text-text-primary" data-id="element-1387">
                      {campaignStats.storeCount}
                    </span>{' '}
                    store{campaignStats.storeCount !== 1 ? 's' : ''}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-text-secondary" data-id="element-1388">
                    <CalendarIcon className="w-4 h-4 text-text-tertiary" data-id="element-1389" />
                    <span className="font-semibold text-text-primary" data-id="element-1390">
                      {campaignStats.weekCount}
                    </span>{' '}
                    week{campaignStats.weekCount !== 1 ? 's' : ''} active
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-text-secondary" data-id="element-1391">
                    <DollarSignIcon className="w-4 h-4 text-text-tertiary" data-id="element-1392" />
                    <span className="font-semibold text-text-primary" data-id="element-1393">
                      {formatCurrency(campaignStats.totalInvestment)}
                    </span>{' '}
                    invested
                  </div>
                </div>}
            </div>
            <Button variant="primary" onClick={() => handleRequestSpace()} className="flex items-center gap-2" data-id="element-1394">
              <SendIcon className="w-4 h-4" data-id="element-1395" />
              Request Media Space
            </Button>
          </div>
        </div>

        <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-8 space-y-8" data-id="element-1396">
          {/* Summary Cards — clickable to scroll to sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1397">
            <button type="button" onClick={() => scrollToSection(campaignsRef)} className="bg-white border border-border rounded-lg p-6 shadow-sm flex items-start gap-4 text-left hover:border-primary/40 hover:shadow-md transition-all cursor-pointer" data-id="element-1398">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-1399">
                <CalendarIcon className="w-6 h-6 text-primary" data-id="element-1400" />
              </div>
              <div data-id="element-1401">
                <p className="text-sm font-medium text-text-secondary mb-1" data-id="element-1402">
                  Active Bookings
                </p>
                <p className="text-3xl font-bold text-text-primary" data-id="element-1403">
                  {myCampaignsByWeek.length}
                </p>
              </div>
            </button>

            <button type="button" onClick={() => scrollToSection(invitationsRef)} className="bg-white border border-border rounded-lg p-6 shadow-sm flex items-start gap-4 text-left hover:border-blue-300 hover:shadow-md transition-all cursor-pointer" data-id="element-1404">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-1405">
                <SendIcon className="w-6 h-6 text-blue-600" data-id="element-1406" />
              </div>
              <div data-id="element-1407">
                <p className="text-sm font-medium text-text-secondary mb-1" data-id="element-1408">
                  Pending Invitations
                </p>
                <p className="text-3xl font-bold text-text-primary" data-id="element-1409">
                  {pendingInvitations.length}
                </p>
              </div>
            </button>

            <button type="button" onClick={() => scrollToSection(requestsRef)} className="bg-white border border-border rounded-lg p-6 shadow-sm flex items-start gap-4 text-left hover:border-purple-300 hover:shadow-md transition-all cursor-pointer" data-id="element-1410">
              <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-1411">
                <FileTextIcon className="w-6 h-6 text-purple-600" data-id="element-1412" />
              </div>
              <div data-id="element-1413">
                <p className="text-sm font-medium text-text-secondary mb-1" data-id="element-1414">
                  My Requests
                </p>
                <p className="text-3xl font-bold text-text-primary" data-id="element-1415">
                  {myRequests.length}
                </p>
              </div>
            </button>

            <button type="button" onClick={() => scrollToSection(campaignsRef)} className="bg-white border border-border rounded-lg p-6 shadow-sm flex items-start gap-4 text-left hover:border-orange-300 hover:shadow-md transition-all cursor-pointer" data-id="element-1416">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${missingArtworkCount > 0 ? 'bg-orange-50' : 'bg-green-50'}`} data-id="element-1417">
                {missingArtworkCount > 0 ? <AlertCircleIcon className="w-6 h-6 text-orange-500" data-id="element-1418" /> : <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-1419" />}
              </div>
              <div data-id="element-1420">
                <p className="text-sm font-medium text-text-secondary mb-1" data-id="element-1421">
                  Artwork Needed
                </p>
                <p className="text-3xl font-bold text-text-primary" data-id="element-1422">
                  {missingArtworkCount}
                </p>
              </div>
            </button>
          </div>

          {/* Booking Calendar — privacy-aware: other brands shown as "Unavailable" */}
          <section data-id="element-1423">
            <div className="flex items-center justify-between mb-4" data-id="element-1424">
              <div data-id="element-1425">
                <h2 className="text-xl font-bold text-text-primary flex items-center gap-2" data-id="element-1426">
                  <CalendarIcon className="w-5 h-5 text-primary" data-id="element-1427" />
                  Browse Availability
                </h2>
                <p className="text-sm text-text-secondary mt-1" data-id="element-1428">
                  View available media positions across all stores. Booked slots
                  by other brands are shown as unavailable to protect
                  confidentiality.
                </p>
              </div>
            </div>
            <DashboardCalendar stores={stores} brandName={brandName} data-id="element-1429" />
          </section>

          {/* Store Availability Browser */}
          <section data-id="element-1430">
            <StoreAvailabilityBrowser stores={stores} onRequestSpace={handleRequestSpace} data-id="element-1431" />
          </section>

          {/* Pending Invitations Section */}
          <section ref={invitationsRef} data-id="element-1432">
            <h2 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2" data-id="element-1433">
              <SendIcon className="w-5 h-5 text-blue-500" data-id="element-1434" />
              Pending Invitations
            </h2>
            <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden" data-id="element-1435">
              {pendingInvitations.length === 0 ? <EmptyState icon={InboxIcon} title="No pending invitations" description="When Woolworths invites you to book media space, invitations will appear here." className="border-none shadow-none" data-id="element-1436" /> : <table className="w-full text-left border-collapse" data-id="element-1437">
                  <thead className="bg-surface border-b border-border" data-id="element-1438">
                    <tr data-id="element-1439">
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1440">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1441">
                        Stores
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1442">
                        Media
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1443">
                        Week
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-right" data-id="element-1444">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border" data-id="element-1445">
                    {pendingInvitations.map(req => <tr key={req.id} className="hover:bg-surface/40 transition-colors" data-id="element-1446">
                        <td className="px-6 py-4" data-id="element-1447">
                          <div className="text-sm font-medium text-text-primary" data-id="element-1448">
                            {req.campaignName || 'Unnamed Campaign'}
                          </div>
                          <div className="text-xs text-text-tertiary mt-1" data-id="element-1449">
                            {req.status === 'brand-accepted' ? 'You accepted — awaiting Woolworths confirmation' : 'Invited by Woolworths'}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1450">
                          <div className="text-sm text-text-secondary" data-id="element-1451">
                            {req.stores.length} store
                            {req.stores.length !== 1 ? 's' : ''}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1452">
                          <div className="flex flex-wrap gap-1.5" data-id="element-1453">
                            {(req.mediaTypes || [req.mediaType]).map(mt => <span key={mt} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-text-secondary bg-surface-warm border border-border rounded-sm" data-id="element-1454">
                                {mediaTypeIcon[mt]}
                                {mt.replace('Digital ', '')}
                              </span>)}
                          </div>
                          <PositionBadges positionIds={req.positionIds} stores={stores} data-id="element-1455" />
                        </td>
                        <td className="px-6 py-4" data-id="element-1456">
                          <div className="text-sm font-medium text-text-primary" data-id="element-1457">
                            Week {req.weekNumber}
                            {req.endWeekNumber && req.endWeekNumber !== req.weekNumber ? ` – ${req.endWeekNumber}` : ''}
                          </div>
                          <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1458">
                            {formatWeekDates(req.weekStartDate, req.weekEndDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-1459">
                          {req.status === 'brand-accepted' ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-sm" data-id="element-1460">
                              <ClockIcon className="w-3 h-3" data-id="element-1461" />
                              Awaiting Woolworths Approval
                            </span> : <Button variant="primary" size="sm" onClick={() => setSelectedInvitation(req)} data-id="element-1462">
                              View & Respond
                            </Button>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>}
            </div>
          </section>

          {/* My Requests Section — always visible now */}
          <section ref={requestsRef} data-id="element-1463">
            <h2 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2" data-id="element-1464">
              <FileTextIcon className="w-5 h-5 text-purple-600" data-id="element-1465" />
              My Requests
            </h2>
            <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden" data-id="element-1466">
              {myRequests.length === 0 ? <EmptyState icon={FileTextIcon} title="No requests yet" description="When you request media space, your submissions will appear here so you can track their status." className="border-none shadow-none" data-id="element-1467" /> : <table className="w-full text-left border-collapse" data-id="element-1468">
                  <thead className="bg-surface border-b border-border" data-id="element-1469">
                    <tr data-id="element-1470">
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1471">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1472">
                        Stores
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1473">
                        Media
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1474">
                        Week
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1475">
                        Status
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-right" data-id="element-1476">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border" data-id="element-1477">
                    {myRequests.map(req => <Fragment key={req.id} data-id="element-1478">
                        <tr className="hover:bg-surface/40 transition-colors cursor-pointer" onClick={() => setExpandedRequestId(expandedRequestId === req.id ? null : req.id)} data-id="element-1479">
                          <td className="px-6 py-4" data-id="element-1480">
                            <div className="flex items-center gap-2" data-id="element-1481">
                              <ChevronDownIcon className={`w-4 h-4 text-text-tertiary transition-transform ${expandedRequestId === req.id ? 'rotate-180' : ''}`} data-id="element-1482" />
                              <div data-id="element-1483">
                                <div className="text-sm font-medium text-text-primary" data-id="element-1484">
                                  {req.campaignName || 'Unnamed Campaign'}
                                </div>
                                <div className="text-xs text-text-tertiary mt-1" data-id="element-1485">
                                  Submitted by you
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-1486">
                            <div className="text-sm text-text-secondary" data-id="element-1487">
                              {req.stores.length} store
                              {req.stores.length !== 1 ? 's' : ''}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-1488">
                            <div className="flex flex-wrap gap-1.5" data-id="element-1489">
                              {(req.mediaTypes || [req.mediaType]).map(mt => <span key={mt} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-text-secondary bg-surface-warm border border-border rounded-sm" data-id="element-1490">
                                  {mediaTypeIcon[mt]}
                                  {mt.replace('Digital ', '')}
                                </span>)}
                            </div>
                            <PositionBadges positionIds={req.positionIds} stores={stores} data-id="element-1491" />
                          </td>
                          <td className="px-6 py-4" data-id="element-1492">
                            <div className="text-sm font-medium text-text-primary" data-id="element-1493">
                              Week {req.weekNumber}
                              {req.endWeekNumber && req.endWeekNumber !== req.weekNumber ? ` – ${req.endWeekNumber}` : ''}
                            </div>
                            <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1494">
                              {formatWeekDates(req.weekStartDate, req.weekEndDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-1495">
                            {req.status === 'pending' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-sm" data-id="element-1496">
                                <ClockIcon className="w-3 h-3" data-id="element-1497" />
                                Awaiting Review
                              </span>}
                            {req.status === 'brand-accepted' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-sm" data-id="element-1498">
                                <ClockIcon className="w-3 h-3" data-id="element-1499" />
                                Awaiting WW Approval
                              </span>}
                            {req.status === 'accepted' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-green-50 text-green-700 border border-green-200 rounded-sm" data-id="element-1500">
                                <CheckCircleIcon className="w-3 h-3" data-id="element-1501" />
                                Approved
                              </span>}
                            {req.status === 'accepted-with-conditions' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-sm" data-id="element-1502">
                                <CheckCircleIcon className="w-3 h-3" data-id="element-1503" />
                                Approved (Conditions)
                              </span>}
                            {req.status === 'rejected' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-red-50 text-red-700 border border-red-200 rounded-sm" data-id="element-1504">
                                <XIcon className="w-3 h-3" data-id="element-1505" />
                                Declined
                              </span>}
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-1506">
                            {req.status === 'pending' && <div className="flex items-center justify-end gap-2" data-id="element-1507">
                                <Button variant="secondary" size="sm" onClick={() => {
                          setEditingRequest(req);
                          setIsRequestModalOpen(true);
                        }} className="flex items-center gap-1.5" data-id="element-1508">
                                  <PencilIcon className="w-3.5 h-3.5" data-id="element-1509" />
                                  Edit
                                </Button>
                                {onRemoveRequest && <Button variant="secondary" size="sm" onClick={e => {
                          e.stopPropagation();
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Withdraw Request',
                            description: 'Are you sure you want to withdraw this request? This cannot be undone.',
                            action: () => {
                              onRemoveRequest(req.id);
                              showToast('Request withdrawn');
                            }
                          });
                        }} className="border-urgent/50 text-urgent hover:bg-urgent/5 flex items-center gap-1.5" data-id="element-1510">
                                    <Trash2Icon className="w-3.5 h-3.5" data-id="element-1511" />
                                    Withdraw
                                  </Button>}
                              </div>}
                          </td>
                        </tr>
                        {expandedRequestId === req.id && <tr data-id="element-1512">
                            <td colSpan={6} className="px-6 py-0 bg-surface-warm/30" data-id="element-1513">
                              <div className="py-4 pl-6 border-l-2 border-primary/20 ml-2 space-y-3" data-id="element-1514">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm" data-id="element-1515">
                                  <div data-id="element-1516">
                                    <div className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-1" data-id="element-1517">
                                      Stores
                                    </div>
                                    <div className="space-y-0.5" data-id="element-1518">
                                      {req.stores.map(s => <div key={s.storeId} className="text-text-primary" data-id="element-1519">
                                          {s.storeName}
                                        </div>)}
                                    </div>
                                  </div>
                                  <div data-id="element-1520">
                                    <div className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-1" data-id="element-1521">
                                      Media Types
                                    </div>
                                    <div className="space-y-0.5" data-id="element-1522">
                                      {(req.mediaTypes || [req.mediaType]).map(mt => <div key={mt} className="flex items-center gap-1.5 text-text-primary" data-id="element-1523">
                                            {mediaTypeIcon[mt]}
                                            {mt}
                                          </div>)}
                                    </div>
                                  </div>
                                  <div data-id="element-1524">
                                    <div className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-1" data-id="element-1525">
                                      Period
                                    </div>
                                    <div className="text-text-primary" data-id="element-1526">
                                      Week {req.weekNumber}
                                      {req.endWeekNumber && req.endWeekNumber !== req.weekNumber ? ` – ${req.endWeekNumber}` : ''}
                                    </div>
                                    <div className="text-text-secondary text-xs" data-id="element-1527">
                                      {formatWeekDates(req.weekStartDate, req.weekEndDate)}
                                    </div>
                                  </div>
                                </div>
                                {req.message && <div data-id="element-1528">
                                    <div className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-1" data-id="element-1529">
                                      Message
                                    </div>
                                    <div className="text-sm text-text-secondary bg-surface-warm p-2 rounded-sm" data-id="element-1530">
                                      {req.message}
                                    </div>
                                  </div>}
                                {req.approvalConditions && <div data-id="element-1531">
                                    <div className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-1" data-id="element-1532">
                                      Conditions from Woolworths
                                    </div>
                                    <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded-sm" data-id="element-1533">
                                      {req.approvalConditions}
                                    </div>
                                  </div>}
                                <div className="text-xs text-text-tertiary" data-id="element-1534">
                                  Submitted{' '}
                                  {new Date(req.createdAt).toLocaleDateString('en-ZA', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                                  {req.respondedAt && <span data-id="element-1535">
                                      {' '}
                                      · Responded{' '}
                                      {new Date(req.respondedAt).toLocaleDateString('en-ZA', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                                    </span>}
                                </div>
                              </div>
                            </td>
                          </tr>}
                      </Fragment>)}
                  </tbody>
                </table>}
            </div>
          </section>

          {/* My Campaigns Section */}
          <section ref={campaignsRef} data-id="element-1536">
            <h2 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2" data-id="element-1537">
              <CalendarIcon className="w-5 h-5 text-text-primary" data-id="element-1538" />
              Your Active Campaigns
            </h2>
            <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden" data-id="element-1539">
              {myCampaignsByWeek.length === 0 ? <EmptyState icon={SearchIcon} title="No active campaigns" description="You don't have any active bookings yet. Contact your Woolworths representative to start planning your next campaign." className="border-none shadow-none" data-id="element-1540" /> : <div className="overflow-x-auto" data-id="element-1541">
                  <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-1542">
                    <thead className="bg-surface border-b border-border" data-id="element-1543">
                      <tr data-id="element-1544">
                        <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1545">
                          Store
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1546">
                          Media Type
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1547">
                          Investment
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1548">
                          Artwork Status
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-right" data-id="element-1549">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border" data-id="element-1550">
                      {myCampaignsByWeek.map(({
                    weekNumber,
                    campaigns
                  }) => <Fragment key={weekNumber} data-id="element-1551">
                          <tr className="bg-surface-warm/50 border-y border-border" data-id="element-1552">
                            <td colSpan={5} className="px-6 py-2" data-id="element-1553">
                              <div className="flex items-center gap-2" data-id="element-1554">
                                <span className="text-sm font-bold text-text-primary" data-id="element-1555">
                                  Week {weekNumber}
                                </span>
                                <span className="text-xs text-text-tertiary" data-id="element-1556">
                                  {formatWeekDates(campaigns[0].slot.weekStartDate, campaigns[0].slot.weekEndDate)}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {campaigns.map(({
                      id,
                      store,
                      slot
                    }) => <tr key={id} className="hover:bg-surface/40 transition-colors" data-id="element-1557">
                              <td className="px-6 py-4 pl-8" data-id="element-1558">
                                <div className="text-sm font-medium text-text-primary cursor-pointer hover:text-primary transition-colors group flex items-center gap-1" onClick={() => onStoreSelect(store)} data-id="element-1559">
                                  {store.name}
                                  <ArrowRightIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1560" />
                                </div>
                                <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1561">
                                  {store.location}
                                </div>
                              </td>
                              <td className="px-6 py-4" data-id="element-1562">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium text-text-secondary bg-surface-warm border border-border rounded-sm" data-id="element-1563">
                                  {mediaTypeIcon[slot.mediaType]}
                                  {slot.mediaType}
                                </span>

                                {/* Find if this slot has conditions from a request */}
                                {(() => {
                          const relatedRequest = brandRequests.find(r => r.brandName === brandName && r.status === 'accepted-with-conditions' && r.weekNumber === slot.weekNumber && r.stores.some(s => s.storeId === store.id));
                          if (relatedRequest?.approvalConditions) {
                            return <div className="mt-2 text-[11px] leading-tight text-requested bg-requested/5 border border-requested/20 p-1.5 rounded" data-id="element-1564">
                                        <span className="font-semibold" data-id="element-1565">
                                          Conditions:
                                        </span>{' '}
                                        {relatedRequest.approvalConditions}
                                      </div>;
                          }
                          return null;
                        })()}
                              </td>
                              <td className="px-6 py-4" data-id="element-1566">
                                <div className="text-sm font-medium text-text-primary" data-id="element-1567">
                                  {formatCurrency(slot.price)}
                                </div>
                              </td>
                              <td className="px-6 py-4" data-id="element-1568">
                                {!slot.artworkStatus || slot.artworkStatus === 'none' ? <Badge variant="removed" className="bg-orange-50 text-orange-700 border-orange-200" data-id="element-1569">
                                    Needs Upload
                                  </Badge> : slot.artworkStatus === 'uploaded' ? <Badge variant="available" className="bg-blue-50 text-blue-700 border-blue-200" data-id="element-1570">
                                    In Review
                                  </Badge> : <Badge variant="available" className="bg-green-50 text-green-700 border-green-200" data-id="element-1571">
                                    Approved
                                  </Badge>}
                              </td>
                              <td className="px-6 py-4 text-right" data-id="element-1572">
                                <Button variant="secondary" size="sm" className="flex items-center gap-1.5 ml-auto" onClick={() => setArtworkModalSlot(slot)} data-id="element-1573">
                                  <ImageIcon className="w-3.5 h-3.5" data-id="element-1574" />
                                  Manage Artwork
                                </Button>
                              </td>
                            </tr>)}
                        </Fragment>)}
                    </tbody>
                  </table>
                </div>}
            </div>
          </section>
        </main>
      </PageTransition>

      {/* Modals — rendered outside PageTransition so fixed positioning works */}
      <ReviewInvitationModal isOpen={!!selectedInvitation} request={selectedInvitation} onClose={() => setSelectedInvitation(null)} onRespond={handleRespondToInvitation} data-id="element-1575" />

      <BrandRequestModal isOpen={isRequestModalOpen} stores={stores} editingDraft={editingRequest} preSelectedStoreIds={preSelectedStoreIds} onClose={() => {
      if (editingRequest) {
        showToast('Request updated successfully');
      }
      setIsRequestModalOpen(false);
      setEditingRequest(null);
      setPreSelectedStoreIds([]);
    }} onSendRequest={(request, sendNow) => {
      onSendRequest(request, sendNow);
      showToast('Request submitted — awaiting Woolworths review');
    }} onUpdateRequest={onUpdateRequest ? (id, request, sendNow) => {
      onUpdateRequest(id, request, sendNow);
      showToast('Request updated successfully');
    } : undefined} isBrandInitiated={true} brandName={brandName} data-id="element-1576" />

      {/* Simple Artwork Modal — rendered outside PageTransition so fixed positioning works */}
      {artworkModalSlot && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" data-id="element-1577">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-slide-up" data-id="element-1578">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border" data-id="element-1579">
              <h2 className="text-lg font-bold text-text-primary" data-id="element-1580">
                Manage Artwork
              </h2>
              <button onClick={() => setArtworkModalSlot(null)} className="text-text-tertiary hover:text-text-primary transition-colors" data-id="element-1581">
                <XIcon className="w-5 h-5" data-id="element-1582" />
              </button>
            </div>
            <div className="p-6 space-y-6" data-id="element-1583">
              <div data-id="element-1584">
                <div className="text-sm font-medium text-text-primary" data-id="element-1585">
                  {artworkModalSlot.mediaType}
                </div>
                <div className="text-xs text-text-secondary" data-id="element-1586">
                  Week {artworkModalSlot.weekNumber}
                </div>
              </div>
              <ArtworkUpload onFileSelect={file => {
            if (file) {
              showToast('Artwork uploaded successfully. It is now in review.');
              setTimeout(() => setArtworkModalSlot(null), 1000);
            }
          }} data-id="element-1587" />
            </div>
            <div className="px-6 py-4 bg-surface-warm border-t border-border flex justify-end" data-id="element-1588">
              <Button variant="ghost" onClick={() => setArtworkModalSlot(null)} data-id="element-1589">
                Close
              </Button>
            </div>
          </div>
        </div>}

      <ConfirmDialog isOpen={confirmDialog.isOpen} title={confirmDialog.title} description={confirmDialog.description} onConfirm={() => {
      confirmDialog.action();
      setConfirmDialog({
        isOpen: false,
        title: '',
        description: '',
        action: () => {}
      });
    }} onCancel={() => setConfirmDialog({
      isOpen: false,
      title: '',
      description: '',
      action: () => {}
    })} data-id="element-1590" />

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-4 right-4 z-50 animate-slide-up" data-id="element-1591">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-id="element-1592">
            {toast.type === 'success' ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-1593" /> : <AlertCircleIcon className="w-5 h-5 text-red-500" data-id="element-1594" />}
            <span className="text-sm font-medium" data-id="element-1595">{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-current opacity-70 hover:opacity-100" data-id="element-1596">
              <XIcon className="w-4 h-4" data-id="element-1597" />
            </button>
          </div>
        </div>}
    </div>;
}